import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  eventLook,
  externalApplyLink,
  hasUnconfirmedDates,
  scheduleText,
  unconfirmedDatesLabel,
} from "./eventDiscoveryState";
import {
  EventOpportunity,
  saveEventOpportunity,
  searchEventOpportunities,
  submitEventOpportunityUrl,
  unsaveEventOpportunity,
  updateHomeZip,
} from "../../services/eventOpportunitiesApi";

const HOME_BASE_KEY = "discover_home_base_v1";
const DISTANCE_CHOICES = [10, 25, 50, 100, 250];

type HomeBase = { zip: string; miles: number };

const readHomeBase = (): HomeBase | null => {
  try {
    const stored = JSON.parse(localStorage.getItem(HOME_BASE_KEY) || "null");
    if (stored && /^\d{5}$/.test(stored.zip)) {
      return {
        zip: stored.zip,
        miles: DISTANCE_CHOICES.includes(Number(stored.miles))
          ? Number(stored.miles)
          : 50,
      };
    }
  } catch {
    // Storage can be blocked or hold something unexpected; start fresh.
  }
  return null;
};

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Not provided";

const formatFee = (item: EventOpportunity) => {
  if (item.boothFeeMin == null && item.boothFeeMax == null)
    return "Fee not provided";
  const money = (value: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: item.currency || "USD",
      maximumFractionDigits: 0,
    }).format(value);
  if (item.boothFeeMin != null && item.boothFeeMax != null) {
    return item.boothFeeMin === item.boothFeeMax
      ? money(item.boothFeeMin)
      : `${money(item.boothFeeMin)}-${money(item.boothFeeMax)}`;
  }
  return money(item.boothFeeMin ?? item.boothFeeMax ?? 0);
};

export default function EventDiscoveryPage() {
  const navigate = useNavigate();
  const requestId = useRef(0);
  const [items, setItems] = useState<EventOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [homeBase, setHomeBase] = useState<HomeBase | null>(readHomeBase);
  const [editingHomeBase, setEditingHomeBase] = useState(false);
  const [zipDraft, setZipDraft] = useState(homeBase?.zip || "");
  const [milesDraft, setMilesDraft] = useState(homeBase?.miles || 50);
  const [zipError, setZipError] = useState("");
  const [savingZip, setSavingZip] = useState(false);
  const [applicationsOpen, setApplicationsOpen] = useState(false);
  const [verified, setVerified] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sort, setSort] = useState("nearest");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitUrl, setSubmitUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    async (nextPage = 1, append = false) => {
      if (!homeBase) {
        setLoading(false);
        return;
      }
      const current = ++requestId.current;
      setLoading(true);
      setError("");
      try {
        const result = await searchEventOpportunities({
          q: search.trim() || undefined,
          city: city.trim() || undefined,
          state: state.trim() || undefined,
          zip: homeBase.zip,
          radiusMiles: homeBase.miles,
          applicationsOpen: applicationsOpen || undefined,
          verified: verified || undefined,
          saved: saved || undefined,
          sort,
          page: nextPage,
          limit: 18,
        });
        if (current !== requestId.current) return;
        setItems((existing) =>
          append ? [...existing, ...result.items] : result.items,
        );
        setPage(result.page);
        setHasMore(result.hasMore);
        setTotal(result.total);
      } catch (loadError) {
        if (current === requestId.current) {
          setError(errorMessage(loadError, "Could not load vendor events."));
        }
      } finally {
        if (current === requestId.current) setLoading(false);
      }
    },
    [applicationsOpen, city, homeBase, saved, search, sort, state, verified],
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => void load(1), 300);
    return () => window.clearTimeout(timeout);
  }, [load]);

  const toggleSave = async (item: EventOpportunity) => {
    const id = item.id || item._id;
    const isSaved = Boolean(item.userState?.saved);
    setItems((current) =>
      current.map((entry) =>
        (entry.id || entry._id) === id
          ? {
              ...entry,
              userState: isSaved
                ? entry.userState
                  ? { ...entry.userState, saved: false }
                  : null
                : { saved: true, status: "saved" },
            }
          : entry,
      ),
    );
    try {
      if (isSaved) await unsaveEventOpportunity(id);
      else await saveEventOpportunity(id);
    } catch (saveError) {
      setError(errorMessage(saveError, "Could not update the saved event."));
      void load(1);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await submitEventOpportunityUrl(submitUrl.trim());
      setSubmitUrl("");
      setShowSubmit(false);
    } catch (submitError) {
      setError(errorMessage(submitError, "Could not submit the public URL."));
    } finally {
      setSubmitting(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setCity("");
    setState("");
    setApplicationsOpen(false);
    setVerified(false);
    setSaved(false);
    setSort("nearest");
  };

  const saveHomeBase = async (event: FormEvent) => {
    event.preventDefault();
    if (!/^\d{5}$/.test(zipDraft)) {
      setZipError("Enter a 5-digit ZIP code.");
      return;
    }
    setSavingZip(true);
    setZipError("");
    try {
      await updateHomeZip(zipDraft);
      const next = { zip: zipDraft, miles: milesDraft };
      try {
        localStorage.setItem(HOME_BASE_KEY, JSON.stringify(next));
      } catch {
        // Not being able to remember it is fine; it is saved to the account.
      }
      setHomeBase(next);
      setEditingHomeBase(false);
    } catch (zipSaveError) {
      setZipError(
        errorMessage(zipSaveError, "That ZIP code could not be used."),
      );
    } finally {
      setSavingZip(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-56px)] bg-slate-50 px-4 py-7 md:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-blue-600">Vendor events</p>
            <h1 className="mt-1 text-3xl font-bold text-slate-900">
              Discover events
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Find public vendor events, inspect their sources, and track
              applications before setting up your event operations.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowSubmit(true)}
            className="rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            Submit event
          </button>
        </header>

        <section
          aria-label="Home base"
          className="mt-6 border-b border-slate-200 pb-5"
        >
          {homeBase && !editingHomeBase ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm font-semibold text-slate-900">
                ZIP {homeBase.zip} · within {homeBase.miles} miles
              </p>
              <button
                type="button"
                onClick={() => {
                  setZipDraft(homeBase.zip);
                  setMilesDraft(homeBase.miles);
                  setZipError("");
                  setEditingHomeBase(true);
                }}
                className="text-sm font-semibold text-blue-700 hover:underline"
              >
                Change
              </button>
            </div>
          ) : (
            <form onSubmit={saveHomeBase}>
              <h2 className="text-lg font-semibold text-slate-900">
                Where do you sell from?
              </h2>
              <p className="mt-1 max-w-xl text-sm text-slate-600">
                Enter your home ZIP code and how far you will travel. We show
                events within that distance, closest first.
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <label className="text-xs font-semibold text-slate-600">
                  ZIP code
                  <input
                    inputMode="numeric"
                    maxLength={5}
                    value={zipDraft}
                    onChange={(event) => {
                      setZipDraft(
                        event.target.value.replace(/\D/g, "").slice(0, 5),
                      );
                      setZipError("");
                    }}
                    placeholder="70401"
                    aria-invalid={Boolean(zipError)}
                    className="mt-1 block w-32 rounded-md border border-slate-300 px-3 py-2.5 text-sm font-normal outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Travel distance
                  <select
                    value={milesDraft}
                    onChange={(event) =>
                      setMilesDraft(Number(event.target.value))
                    }
                    className="mt-1 block rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm font-normal outline-none focus:border-blue-500"
                  >
                    {DISTANCE_CHOICES.map((miles) => (
                      <option key={miles} value={miles}>
                        {miles} miles
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="submit"
                  disabled={savingZip}
                  className="rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-slate-400"
                >
                  {savingZip ? "Saving..." : "Show events near me"}
                </button>
                {homeBase && (
                  <button
                    type="button"
                    onClick={() => setEditingHomeBase(false)}
                    className="px-2 py-2.5 text-sm font-semibold text-slate-600 hover:underline"
                  >
                    Cancel
                  </button>
                )}
              </div>
              {zipError && (
                <p
                  role="alert"
                  className="mt-2 text-sm font-medium text-red-700"
                >
                  {zipError}
                </p>
              )}
            </form>
          )}
        </section>

        {homeBase && (
          <>
            <section
              aria-label="Discovery filters"
              className="mt-6 border-b border-slate-200 pb-5"
            >
              <div className="grid gap-3 md:grid-cols-[minmax(240px,2fr)_1fr_90px_160px]">
                <label className="text-xs font-semibold text-slate-600">
                  Search
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Event, city, organizer, category"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm font-normal outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  City
                  <input
                    value={city}
                    onChange={(event) => setCity(event.target.value)}
                    placeholder="Any city"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm font-normal outline-none focus:border-blue-500"
                  />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  State
                  <input
                    value={state}
                    onChange={(event) =>
                      setState(event.target.value.toUpperCase().slice(0, 2))
                    }
                    placeholder="LA"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm font-normal outline-none focus:border-blue-500"
                  />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Sort
                  <select
                    value={sort}
                    onChange={(event) => setSort(event.target.value)}
                    className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm font-normal outline-none focus:border-blue-500"
                  >
                    <option value="recommended">Recommended</option>
                    <option value="nearest">Nearest</option>
                    <option value="deadline">Application deadline</option>
                    <option value="newest">Newly discovered</option>
                    <option value="lowest-fee">Lowest booth fee</option>
                    <option value="recently-verified">Recently verified</option>
                  </select>
                </label>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-700">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={applicationsOpen}
                    onChange={(event) =>
                      setApplicationsOpen(event.target.checked)
                    }
                  />{" "}
                  Applications accepting interest
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={verified}
                    onChange={(event) => setVerified(event.target.checked)}
                  />{" "}
                  Verified only
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={saved}
                    onChange={(event) => setSaved(event.target.checked)}
                  />{" "}
                  Saved only
                </label>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="font-semibold text-blue-700 hover:underline"
                >
                  Reset filters
                </button>
              </div>
            </section>

            <div className="mt-5 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                {loading && !items.length ? "Searching..." : `${total} events`}
              </p>
              {error && (
                <p role="alert" className="text-sm font-medium text-red-700">
                  {error}
                </p>
              )}
            </div>

            {!loading && !error && items.length === 0 ? (
              <section className="py-20 text-center">
                <h2 className="text-xl font-semibold text-slate-900">
                  No matching events
                </h2>
                <p className="mx-auto mt-2 max-w-lg text-sm text-slate-600">
                  Broaden your location or filters, or submit a public event
                  link for review.
                </p>
              </section>
            ) : (
              <section
                aria-label="Vendor event results"
                className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3"
              >
                {items.map((item) => {
                  const id = item.id || item._id;
                  return (
                    <article
                      key={id}
                      className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
                    >
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt=""
                          className="aspect-[2.4/1] w-full object-cover"
                        />
                      ) : (
                        <div
                          aria-hidden="true"
                          className="h-1.5 w-full"
                          style={{
                            backgroundColor: eventLook(item.categories).color,
                          }}
                        />
                      )}
                      <div className="p-4">
                        {!item.imageUrl && (
                          <span
                            className="mb-2 inline-block rounded-md px-2 py-0.5 text-xs font-semibold"
                            style={{
                              backgroundColor: `${eventLook(item.categories).color}22`,
                              color: eventLook(item.categories).color,
                            }}
                          >
                            {eventLook(item.categories).label}
                          </span>
                        )}
                        <div className="flex items-start justify-between gap-3">
                          <button
                            type="button"
                            onClick={() => navigate(`/discover/${id}`)}
                            className="text-left"
                          >
                            <h2 className="line-clamp-2 text-lg font-semibold text-slate-900 hover:text-blue-700">
                              {item.name}
                            </h2>
                          </button>
                          <button
                            type="button"
                            aria-label={
                              item.userState?.saved
                                ? "Remove saved event"
                                : "Save event"
                            }
                            onClick={() => void toggleSave(item)}
                            className="rounded p-1 text-xl text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                          >
                            {item.userState?.saved ? "●" : "○"}
                          </button>
                        </div>
                        <p className="mt-2 text-sm text-slate-600">
                          {[item.city, item.state].filter(Boolean).join(", ") ||
                            "Location not provided"}
                          {item.distanceMiles != null
                            ? ` · ${item.distanceMiles} mi`
                            : ""}
                        </p>
                        <p className="mt-1 text-sm font-medium text-slate-800">
                          {hasUnconfirmedDates(item) ? (
                            <>
                              <span className="font-semibold text-red-700">
                                {unconfirmedDatesLabel(item)}
                              </span>
                              {scheduleText(item)
                                ? ` · ${scheduleText(item)}`
                                : ""}
                            </>
                          ) : (
                            formatDate(item.startAt)
                          )}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold capitalize text-slate-700">
                            {item.applicationStatus === "unknown"
                              ? "Status unconfirmed"
                              : item.applicationStatus.replaceAll("-", " ")}
                          </span>
                          {item.match && (
                            <span className="rounded-md bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                              {item.match.score}% match
                            </span>
                          )}
                        </div>
                        <div className="mt-4 flex items-end justify-between border-t border-slate-100 pt-3 text-sm">
                          <span className="font-semibold text-slate-900">
                            {formatFee(item)}
                          </span>
                          <a
                            href={externalApplyLink(item).url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Apply to ${item.name} outside the app`}
                            className="font-semibold text-blue-700 hover:underline"
                          >
                            Apply ↗
                          </a>
                          <span
                            className={
                              item.applicationDeadline
                                ? "text-red-700"
                                : "text-slate-500"
                            }
                          >
                            {item.applicationDeadline
                              ? `Apply by ${formatDate(item.applicationDeadline)}`
                              : "Deadline not provided"}
                          </span>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </section>
            )}
            {hasMore && (
              <div className="mt-7 text-center">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => void load(page + 1, true)}
                  className="rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 hover:border-blue-400"
                >
                  {loading ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showSubmit && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="submit-title"
        >
          <form
            onSubmit={submit}
            className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
          >
            <div className="flex items-start justify-between">
              <div>
                <h2
                  id="submit-title"
                  className="text-xl font-semibold text-slate-900"
                >
                  Submit public event
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  We verify the source before publishing any listing facts.
                </p>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setShowSubmit(false)}
                className="text-2xl text-slate-500"
              >
                ×
              </button>
            </div>
            <label className="mt-5 block text-sm font-semibold text-slate-700">
              Event or vendor application URL
              <input
                required
                type="url"
                value={submitUrl}
                onChange={(event) => setSubmitUrl(event.target.value)}
                placeholder="https://example.org/vendor-application"
                className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2.5 font-normal outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </label>
            <p className="mt-3 text-xs leading-5 text-slate-500">
              Private groups, login-gated pages, CAPTCHAs, and private network
              addresses are not accessed.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="mt-5 w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-slate-400"
            >
              {submitting ? "Submitting..." : "Submit for review"}
            </button>
          </form>
        </div>
      )}
    </main>
  );
}
